import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import { MessageCircle, X, Send, ChevronLeft, LifeBuoy, Home } from "lucide-react";

export default function ChatWidget() {
  const { user } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // 'list' | 'support' | conversationId
  const [convs, setConvs] = useState([]);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [unread, setUnread] = useState({ total: 0, dm: 0, support: 0 });
  const [activeTitle, setActiveTitle] = useState("");
  const endRef = useRef(null);

  const canChat = user && (user.role === "customer" || user.role === "broker" || user.role === "owner");

  const loadUnread = async () => { try { const { data } = await api.get("/messages/unread"); setUnread(data); } catch (e) { console.debug("unread load failed", e); } };
  const loadConvs = async () => { try { const { data } = await api.get("/dm/conversations"); setConvs(data.conversations); } catch (e) { console.debug("conversations load failed", e); } };
  const loadSupport = async () => { try { const { data } = await api.get("/chat/messages"); setMsgs(data.messages); loadUnread(); } catch (e) { console.debug("support load failed", e); } };
  const loadDM = async (id) => { try { const { data } = await api.get(`/dm/conversations/${id}/messages`); setMsgs(data.messages); setActiveTitle(data.property_title || ""); loadUnread(); } catch (e) { console.debug("DM load failed", e); } };

  // Poll unread badge continuously (even when closed)
  useEffect(() => {
    if (!canChat) return;
    loadUnread();
    const id = setInterval(loadUnread, 5000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [canChat]);

  // Poll the active view
  useEffect(() => {
    if (!open || !canChat) return;
    let fn = null;
    if (view === "list") { loadConvs(); fn = loadConvs; }
    else if (view === "support") { loadSupport(); fn = loadSupport; }
    else { loadDM(view); fn = () => loadDM(view); }
    const id = setInterval(fn, 4000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [open, view, canChat]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const send = async () => {
    if (!text.trim()) return;
    const body = { text: text.trim() };
    setText("");
    if (view === "support") { await api.post("/chat/messages", body); loadSupport(); }
    else { await api.post(`/dm/conversations/${view}/messages`, body); loadDM(view); }
  };

  const openConv = (id, title) => { setActiveTitle(title); setView(id); };

  if (!canChat) return null;

  const headerTitle = view === "list" ? "Messages" : view === "support" ? "Support" : activeTitle || "Chat";

  return (
    <>
      <button
        onClick={() => { setOpen(!open); if (!open) setView("list"); }}
        data-testid="chat-toggle"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-brand-terracotta hover:bg-brand-terracottaHover text-white flex items-center justify-center shadow-lg transition-transform hover:scale-105"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
        {!open && unread.total > 0 && (
          <span data-testid="chat-unread-badge" className="absolute -top-1 -right-1 min-w-[22px] h-[22px] px-1.5 rounded-full bg-white text-brand-terracotta text-xs font-bold flex items-center justify-center border-2 border-brand-terracotta">
            {unread.total > 99 ? "99+" : unread.total}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 w-[92vw] max-w-sm h-[30rem] bg-card border border-brand-line rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="chat-panel">
          <div className="bg-brand-emerald text-white px-4 py-3 font-heading font-semibold flex items-center gap-2">
            {view !== "list" && <button onClick={() => setView("list")} data-testid="chat-back" className="opacity-90 hover:opacity-100"><ChevronLeft className="w-5 h-5" /></button>}
            <span className="flex-1 truncate">{headerTitle}</span>
          </div>

          {view === "list" ? (
            <div className="flex-1 overflow-y-auto p-2 no-scrollbar" data-testid="chat-conv-list">
              <button onClick={() => setView("support")} data-testid="conv-support" className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-secondary text-left">
                <div className="w-10 h-10 rounded-full bg-brand-terracotta/10 flex items-center justify-center shrink-0"><LifeBuoy className="w-5 h-5 text-brand-terracotta" /></div>
                <div className="flex-1 min-w-0"><div className="font-semibold text-brand-ink text-sm">Support (Admin)</div><div className="text-xs text-brand-muted truncate">Chat with the My Property team</div></div>
                {unread.support > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-brand-terracotta text-white text-xs font-bold flex items-center justify-center">{unread.support}</span>}
              </button>
              <div className="text-[11px] uppercase tracking-wider text-brand-muted px-3 mt-2 mb-1">Direct Messages</div>
              {convs.length === 0 && <p className="text-sm text-brand-muted text-center py-6 px-3">No conversations yet. Make an enquiry to start chatting with a broker.</p>}
              {convs.map((c) => (
                <button key={c.id} onClick={() => openConv(c.id, c.property_title)} data-testid={`conv-${c.id}`} className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-secondary text-left">
                  <div className="w-10 h-10 rounded-full bg-brand-emerald/10 flex items-center justify-center shrink-0"><Home className="w-5 h-5 text-brand-emerald" /></div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-brand-ink text-sm truncate">{c.title}</div>
                    <div className="text-xs text-brand-muted truncate">{c.property_title}{c.last_text ? ` · ${c.last_text}` : ""}</div>
                  </div>
                  {c.unread > 0 && <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-brand-emerald text-white text-xs font-bold flex items-center justify-center" data-testid={`conv-unread-${c.id}`}>{c.unread}</span>}
                </button>
              ))}
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar">
                {msgs.length === 0 && <p className="text-sm text-brand-muted text-center mt-8">{t("chat.empty")}</p>}
                {msgs.map((m) => {
                  const mine = m.sender_id === user.id;
                  const isAdmin = m.sender_role === "admin" || m.sender_role === "subadmin";
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[80%] px-3 py-2 rounded-2xl text-sm ${mine ? "bg-brand-emerald text-white rounded-br-sm" : isAdmin ? "bg-brand-terracotta/10 text-brand-ink rounded-bl-sm" : "bg-secondary text-brand-ink rounded-bl-sm"}`}>
                        {!mine && <div className="text-[10px] font-semibold opacity-70 mb-0.5">{m.sender_name}</div>}
                        {m.text}
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>
              <div className="p-3 border-t border-brand-line flex gap-2">
                <input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t("chat.placeholder")}
                  data-testid="chat-input"
                  className="flex-1 px-3 py-2 rounded-full border border-brand-line text-sm outline-none focus:border-brand-emerald"
                />
                <button onClick={send} data-testid="chat-send" className="w-10 h-10 rounded-full bg-brand-emerald text-white flex items-center justify-center shrink-0">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
